import type { Language } from "@marshmallow-duel/simulation";

const en = {
  "mode.title": "Choose game mode",
  "mode.local": "Local game",
  "mode.localHint": "Two to four players on this device",
  "mode.online": "Online game",
  "mode.onlineHint": "Host or join a room for up to four players",
  "menu.begin": "Begin match",
  "menu.loadPlayers": "Load fighters",
  "menu.editPlayers": "Edit fighters",
  "menu.hall": "Hall of fame",
  "menu.editor": "Map editor",
  "menu.controls": "Controls",
  "menu.abilities": "Abilities",
  "menu.settings": "Settings",
  "menu.authors": "Authors",
  "menu.exit": "Exit",
  "menu.language": "English",
  "common.back": "Back",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.random": "Random",
  "common.ready": "Ready",
  "maps.title": "Select map",
  "maps.original": "Original maps",
  "maps.library": "Online map library",
  "maps.loading": "Loading maps…",
  "maps.empty": "No published maps for this player count yet.",
  "maps.by": "by",
  "players.title": "Edit fighters",
  "players.loadTitle": "Load fighters",
  "players.loadHelp": "Choose fighters in player-slot order.",
  "players.editHelp": "The original six local fighter profiles are stored in this browser.",
  "players.score": "Score",
  "players.slot": "Slot",
  "players.notSelected": "Not selected",
  "players.resetScores": "Reset scores",
  "players.resetAll": "Reset fighters",
  "players.name": "Name",
  "players.color": "Color",
  "players.skin": "Character",
  "settings.title": "Settings",
  "settings.game": "Game and players",
  "settings.viewSound": "Display and sound",
  "settings.players": "Number of players",
  "settings.abilitiesCount": "Abilities in the room",
  "settings.winnerPoints": "Winning score",
  "settings.randomSkull": "Random skull",
  "settings.fighterAbility": "Fighter abilities",
  "settings.showScore": "Show scores",
  "settings.saveWinner": "Save winner screenshot",
  "settings.ropes": "Ropes",
  "settings.shields": "Shields",
  "settings.physics": "Physics",
  "settings.sound": "Sound effects",
  "settings.music": "Music",
  "settings.raining": "Rain",
  "settings.screaming": "Screaming",
  "settings.notifier": "Announcer",
  "settings.showFps": "Show FPS",
  "settings.oldTextures": "Old textures",
  "settings.gamepads": "Gamepads",
  "abilities.title": "Abilities",
  "abilities.toggleAll": "Toggle all",
  "controls.title": "Controls",
  "controls.player1": "Player 1: A/D, W, S, Q, 1, F",
  "controls.player2": "Player 2: arrows, Shift, Enter, Ctrl",
  "controls.player3": "Player 3: J/L, I, K, P, O, U",
  "controls.player4": "Player 4: num 4/6, 8, 5, 0, 9, 7",
  "hall.title": "Hall of fame",
  "authors.title": "Authors",
  "authors.text": "Original game and web conversion: Jakub Kučera",
  "game.map": "Map:",
  "game.continue": "Press ENTER to continue",
  "game.restart": "Restart map (R)",
  "game.selectMap": "Select map (M)",
  "game.wins": "wins!",
  "game.draw": "Draw!",
  "game.lastRound": "LAST ROUND",
  "game.leave": "Back to menu",
  "online.title": "Online game",
  "online.coming": "Room, WebRTC and map-library services are being connected to this faithful local build.",
  "online.loading": "Connecting to Splatch…",
  "online.splatchTitle": "Splatch account",
  "online.splatchRequired": "Online play and publishing maps require your central Splatch account.",
  "online.splatchFailed": "Splatch sign-in expired or could not be completed.",
  "online.continueSplatch": "Continue with Splatch",
  "online.hostLeft": "The room owner left the game.",
  "online.disconnected": "Connection to the game service was lost.",
  "online.connecting": "Connecting players…",
  "online.connectionLost": "The direct connection between players was lost.",
  "online.roomCode": "Room code",
  "online.waiting": "Waiting for player…",
  "online.start": "Start match",
  "online.profile": "Fighter",
  "online.create": "Create room",
  "online.codePlaceholder": "ROOM CODE",
  "online.join": "Join room",
  "online.signOut": "Sign out",
  "online.error.roomNotFound": "That room does not exist or has already started.",
  "online.error.roomFull": "The room is full.",
  "online.error.playersNotReady": "All selected player slots must be occupied and ready.",
  "online.error.hostOnly": "Only the room owner can change this setting.",
  "online.error.serverFull": "The online service is currently full.",
  "online.error.invalidSettings": "These room settings or map are invalid.",
  "online.error.alreadyInRoom": "You are already in a room.",
  "online.error.rateLimited": "Too many requests. Please wait a moment.",
  "online.error.generic": "The online service rejected this action.",
  "editor.canvas": "Map editing area",
  "editor.new": "New map",
  "editor.load": "Load map",
  "editor.save": "Save map",
  "editor.publish": "Publish",
  "editor.publishing": "Publishing…",
  "editor.splatchRequired": "Sign in through Splatch before publishing the map.",
  "editor.publishFailed": "The map could not be published. Its name may already be in use.",
  "editor.test": "Test",
  "editor.name": "Map name",
  "editor.description": "Description",
  "editor.players": "Players",
  "editor.abilities": "Abilities",
  "editor.globalAbilities": "Use abilities from game settings",
  "editor.grid": "Grid"
} as const;

type TranslationKey = keyof typeof en;

const cz: Record<TranslationKey, string> = {
  "mode.title": "Vyberte režim hry",
  "mode.local": "Lokální hra",
  "mode.localHint": "Dva až čtyři hráči na tomto zařízení",
  "mode.online": "Online hra",
  "mode.onlineHint": "Založte nebo otevřete místnost až pro čtyři hráče",
  "menu.begin": "Začít zápas",
  "menu.loadPlayers": "Načíst bojovníky",
  "menu.editPlayers": "Upravit bojovníky",
  "menu.hall": "Síň slávy",
  "menu.editor": "Editor map",
  "menu.controls": "Ovládání",
  "menu.abilities": "Schopnosti",
  "menu.settings": "Nastavení",
  "menu.authors": "Autoři",
  "menu.exit": "Konec",
  "menu.language": "Čeština",
  "common.back": "Zpět",
  "common.save": "Uložit",
  "common.cancel": "Zrušit",
  "common.random": "Náhodná mapa",
  "common.ready": "Připraven",
  "maps.title": "Výběr mapy",
  "maps.original": "Původní mapy",
  "maps.library": "Online knihovna map",
  "maps.loading": "Načítám mapy…",
  "maps.empty": "Pro tento počet hráčů zatím není publikovaná žádná mapa.",
  "maps.by": "od",
  "players.title": "Upravit bojovníky",
  "players.loadTitle": "Načíst bojovníky",
  "players.loadHelp": "Vyber bojovníky v pořadí herních míst.",
  "players.editHelp": "Původních šest lokálních profilů se ukládá v tomto prohlížeči.",
  "players.score": "Skóre",
  "players.slot": "Místo",
  "players.notSelected": "Nevybráno",
  "players.resetScores": "Resetovat skóre",
  "players.resetAll": "Resetovat bojovníky",
  "players.name": "Jméno",
  "players.color": "Barva",
  "players.skin": "Postava",
  "settings.title": "Nastavení",
  "settings.game": "Hra a hráči",
  "settings.viewSound": "Zobrazení a zvuk",
  "settings.players": "Počet hráčů",
  "settings.abilitiesCount": "Počet schopností v místnosti",
  "settings.winnerPoints": "Výherní skóre",
  "settings.randomSkull": "Náhodná lebka",
  "settings.fighterAbility": "Schopnosti hráčů",
  "settings.showScore": "Zobrazit skóre",
  "settings.saveWinner": "Ukládat snímek vítěze",
  "settings.ropes": "Lana",
  "settings.shields": "Štíty",
  "settings.physics": "Fyzika",
  "settings.sound": "Zvukové efekty",
  "settings.music": "Hudba",
  "settings.raining": "Déšť",
  "settings.screaming": "Křik",
  "settings.notifier": "Hlasatel",
  "settings.showFps": "Zobrazit FPS",
  "settings.oldTextures": "Staré textury",
  "settings.gamepads": "Gamepady",
  "abilities.title": "Schopnosti",
  "abilities.toggleAll": "Přepnout vše",
  "controls.title": "Ovládání",
  "controls.player1": "Hráč 1: A/D, W, S, Q, 1, F",
  "controls.player2": "Hráč 2: šipky, Shift, Enter, Ctrl",
  "controls.player3": "Hráč 3: J/L, I, K, P, O, U",
  "controls.player4": "Hráč 4: num 4/6, 8, 5, 0, 9, 7",
  "hall.title": "Síň slávy",
  "authors.title": "Autoři",
  "authors.text": "Původní hra a webová verze: Jakub Kučera",
  "game.map": "Mapa:",
  "game.continue": "Stiskněte ENTER pro pokračování",
  "game.restart": "Restartovat mapu (R)",
  "game.selectMap": "Vybrat mapu (M)",
  "game.wins": "vítězí!",
  "game.draw": "Remíza!",
  "game.lastRound": "POSLEDNÍ KOLO",
  "game.leave": "Zpět do menu",
  "online.title": "Online hra",
  "online.coming": "K této věrné lokální verzi právě připojuji místnosti, WebRTC a online knihovnu map.",
  "online.loading": "Připojuji ke Splatch…",
  "online.splatchTitle": "Účet Splatch",
  "online.splatchRequired": "Online hraní a publikování map vyžaduje tvůj centrální účet Splatch.",
  "online.splatchFailed": "Přihlášení přes Splatch vypršelo nebo se nepodařilo dokončit.",
  "online.continueSplatch": "Pokračovat přes Splatch",
  "online.hostLeft": "Zakladatel místnost opustil.",
  "online.disconnected": "Spojení s herní službou bylo přerušeno.",
  "online.connecting": "Propojuji hráče…",
  "online.connectionLost": "Přímé spojení mezi hráči bylo přerušeno.",
  "online.roomCode": "Kód místnosti",
  "online.waiting": "Čekám na hráče…",
  "online.start": "Spustit zápas",
  "online.profile": "Bojovník",
  "online.create": "Založit místnost",
  "online.codePlaceholder": "KÓD MÍSTNOSTI",
  "online.join": "Připojit se",
  "online.signOut": "Odhlásit",
  "online.error.roomNotFound": "Místnost neexistuje nebo už byl zápas spuštěn.",
  "online.error.roomFull": "Místnost je plná.",
  "online.error.playersNotReady": "Všechna zvolená místa musí být obsazená a připravená.",
  "online.error.hostOnly": "Toto nastavení může změnit jen zakladatel místnosti.",
  "online.error.serverFull": "Online služba je právě plná.",
  "online.error.invalidSettings": "Nastavení místnosti nebo mapa nejsou platné.",
  "online.error.alreadyInRoom": "Už jsi v jiné místnosti.",
  "online.error.rateLimited": "Příliš mnoho požadavků. Chvíli počkej.",
  "online.error.generic": "Online služba tuto akci odmítla.",
  "editor.canvas": "Plocha editoru map",
  "editor.new": "Nová mapa",
  "editor.load": "Načíst mapu",
  "editor.save": "Uložit mapu",
  "editor.publish": "Publikovat",
  "editor.publishing": "Publikuji…",
  "editor.splatchRequired": "Před publikováním mapy se přihlas přes Splatch.",
  "editor.publishFailed": "Mapu se nepodařilo publikovat. Její název už může být použitý.",
  "editor.test": "Testovat",
  "editor.name": "Název mapy",
  "editor.description": "Popis",
  "editor.players": "Hráči",
  "editor.abilities": "Schopnosti",
  "editor.globalAbilities": "Použít schopnosti z nastavení hry",
  "editor.grid": "Mřížka"
};

const catalogs: Record<Language, Record<TranslationKey, string>> = { en, cz };

export const translate = (language: Language, key: TranslationKey): string => catalogs[language][key];
export type { TranslationKey };
